import React, { KeyboardEvent, PureComponent } from "react";

import { createPicassoPainting, createWrapPicassoMapStateToProps, IPoint } from "@multiweb/picasso";
import { AlignItems, ButtonBackgroundColor, IconButton, IconColor, Layout, SplitVerticalPanelLight } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { bemClassNames, createQA, Key } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { WorkflowEditorMode } from "../enums";
import { getPicassoState } from "../selectors";
import { ISet, IVariable } from "../types";
import { UiActivity } from "../UiActivity";
import { UiTransition } from "../UiTransition";
import { UiWorkflow } from "../UiWorkflow";
import { WorkflowManager } from "../WorkflowManager";
import { Workflow } from "../workflowModel";
import { xmlToWfStatus, xmlToWorkflow } from "../workflowParser";
import { linkRenderer, moveableRenderer } from "../WorkflowPicassoComponentsProvider";
import { PropertyPanelContainer } from "./propertyPanel/PropertyPanelContainer";
import { ToolbarPanel } from "./toolbarPanel";
import { WfMessageWindowHandler } from "./WfMessageWindowHandler";

const { generateUUID } = bridge;

export type WorkflowEditorComponentProps = {
    readonly hostKey: string;
    readonly workflow: Workflow;
    readonly wfStatusXml?: string;
    readonly selectedSetId: string;
    readonly sets: readonly ISet[];
    readonly wfEditorMode: WorkflowEditorMode;
    readonly saveWorkflow: () => Promise<boolean>;
    readonly reloadWorkflow: () => Promise<string>;
    readonly enableStatusView: (variables: readonly IVariable[]) => void;
    readonly enableViewerMode: () => void;
    readonly updateWorkflowView: (setId: string, isNonEditChange?: boolean) => void;
    readonly setTouched: (touched: boolean) => void;
    readonly setIsWfEdited: (isEdited: boolean) => Promise<void>;
    readonly createTransition: () => void;
    readonly selectAllWFActivities: () => void;
    readonly deleteSelectedWfEntities: () => void;
    readonly addSetText: string;
    readonly newSetText: string;
};

const classNames = bemClassNames("wf-editor-component");
const wfEditorStyle = classNames();
const wfCanvasCardStyle = classNames("canvas-card");
const tabsContainer = classNames("tabsDivContainer");
const tabsList = classNames("tabsList");

const wfEditorComponentDataQA = createQA("wf-editor-component");
const tabsContainerDataQA = createQA("tabs-container");

const minSizePercentagesValues = [60, 15];
const defaultPercentagesValues = [80, 20];

export class WorkflowEditorComponent extends PureComponent<WorkflowEditorComponentProps> {
    private uiWorkflow: UiWorkflow;
    private wrapMapState = createWrapPicassoMapStateToProps(getPicassoState, this.props.hostKey);
    private PicassoEditor = createPicassoPainting(this.wrapMapState);
    private editorRef: React.RefObject<HTMLDivElement> = React.createRef();

    constructor(props: WorkflowEditorComponentProps) {
        super(props);

        this.uiWorkflow = new UiWorkflow(props.workflow, undefined, this.editorRef, props.setIsWfEdited);

        if (props.wfEditorMode === WorkflowEditorMode.StatusViewer) {
            if (!props.wfStatusXml)
                throw new Error("Cannot initialize workflow editor in Status Viewer mode without wf status xml!");

            const wfStatus = xmlToWfStatus(props.wfStatusXml);
            this.uiWorkflow.workflow.applyWorkflowStatus(wfStatus);
            props.enableStatusView(wfStatus.variables);
        } else if (this.props.wfEditorMode === WorkflowEditorMode.Viewer)
            this.props.enableViewerMode();

        WorkflowManager.uiWorkflows[props.hostKey] = this.uiWorkflow;
        props.updateWorkflowView(this.uiWorkflow.uiSet.set.id, true);
    }

    public override render(): JSX.Element {
        const { uiWorkflow, addSet, onReloadClick, handleKeyUp, handleKeyDown } = this;
        const { addSetText, hostKey, sets, selectedSetId, wfEditorMode, saveWorkflow, createTransition, deleteSelectedWfEntities, updateWorkflowView } = this.props;

        return (
            <div ref={this.editorRef} {...wfEditorComponentDataQA} className={wfEditorStyle}>
                <SplitVerticalPanelLight minSizePercentages={minSizePercentagesValues} defaultPercentages={defaultPercentagesValues}>
                    <div className={wfCanvasCardStyle}>
                        <div {...tabsContainerDataQA} className={tabsContainer}>
                            <ul className={tabsList}>
                                {sets.map(({ id, name }) => (
                                    <li key={id} {...createQA(`tab-${id}`)} onClick={() => updateWorkflowView(id, true)} className={classNames("tab", { "active": selectedSetId === id })}>{name}</li>
                                ))}
                                {wfEditorMode == WorkflowEditorMode.Editor && <Layout alignItems={AlignItems.center}><IconButton color={IconColor.colorPrimary} iconCode={IconCode.uiPlus} onClick={addSet} title={addSetText} backgroundColor={ButtonBackgroundColor.transparent} /></Layout>}
                            </ul>
                        </div>
                        <ToolbarPanel {...{ hostKey, uiWorkflow, saveWorkflow, onReloadClick, createTransition, deleteSelectedWfEntities }} />
                        <this.PicassoEditor
                            onKeyUp={handleKeyUp}
                            onKeyDown={handleKeyDown}
                            picassoInstanceKey={hostKey}
                            onComponentChange={this.handleComponentChange}
                            onLinkChange={this.handleLinkChange}
                            wrapMapStateToProps={this.wrapMapState}
                            renderComponent={moveableRenderer}
                            renderLink={linkRenderer}
                            gridIndex={10} />
                    </div>
                    <PropertyPanelContainer hostKey={hostKey} />
                </SplitVerticalPanelLight>
                <WfMessageWindowHandler {...{ hostKey }} />
            </div>
        );
    }

    private handleKeyUp = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (e.key === Key.Delete) {
            this.props.deleteSelectedWfEntities();
        }

        if (e.key === Key.Space)
            this.props.createTransition();

        if (e.key.toLowerCase() === "a" && e.ctrlKey)
            this.props.selectAllWFActivities();
    };

    private handleKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
        if (e.key.toLowerCase() === "a" && e.ctrlKey) {
            e.preventDefault();
        }
    };

    private addSet = () => {
        const workflow = this.uiWorkflow.workflow;
        workflow.addSet(this.props.newSetText, "", generateUUID());
        this.props.updateWorkflowView(workflow.sets[workflow.sets.length - 1].id);
    };

    private handleComponentChange = (changedComponentIndex: number, newX: number, newY: number, newWidth: number, newHeight: number) => {
        const components = [...this.uiWorkflow.picassoModel.components];
        const { setId, parent, activity } = components[changedComponentIndex] as UiActivity; // přetypování je bezpečné, nikdy nepřijde UiSet
        components[changedComponentIndex] = new UiActivity(changedComponentIndex, newX, newY, newWidth, newHeight, setId, parent, activity);
        this.uiWorkflow.picassoModel.components = components;
        this.props.setTouched(true);
    };

    private handleLinkChange = (changedLinkIndex: number, newPoints: readonly IPoint[]) => {
        const links = [...this.uiWorkflow.picassoModel.links];
        const { startComponentIndex, endComponentIndex, transition } = links[changedLinkIndex] as UiTransition;// přetypování je bezpečné, nemáme jiné implementace než UiTransition
        links[changedLinkIndex] = new UiTransition(changedLinkIndex, newPoints, startComponentIndex, endComponentIndex, transition);
        this.uiWorkflow.picassoModel.links = links;
        this.props.setTouched(true);
    };

    private onReloadClick = async () => {
        const newWorkflowDefinition = await this.props.reloadWorkflow();
        const newWorkflow = await xmlToWorkflow(newWorkflowDefinition);
        const newSelectedSet = newWorkflow.sets.find((set) => set.id === this.props.selectedSetId);

        this.uiWorkflow = new UiWorkflow(newWorkflow, newSelectedSet?.id, this.editorRef, this.props.setIsWfEdited);
        WorkflowManager.uiWorkflows[this.props.hostKey] = this.uiWorkflow;

        this.props.updateWorkflowView(this.uiWorkflow.uiSet.set.id, true);
        this.props.setTouched(false);
    };
}
